import {
  errorTypeSchema,
  jsonSchemaDirectionSchema,
  jsonSchemaSourceSchema,
  standardJsonSchemaSupportSchema,
  jsonSchemaTargetSchema,
  optimizeTypeSchema,
  stringFormatSchema,
} from "@schema-benchmarks/schemas";
import type { OneOf } from "@schema-benchmarks/utils";
import { unsafeFromEntries } from "@schema-benchmarks/utils";
import * as v from "valibot";

export const dataTypeSchema = v.picklist(["invalid", "valid"]);
export type DataType = v.InferOutput<typeof dataTypeSchema>;

export const baseBenchResultSchema = v.object({
  id: v.string(),
  libraryName: v.string(),
  version: v.string(),
  note: v.optional(v.string()),
  snippet: v.string(),
  throws: v.optional(v.boolean()),
  mean: v.number(),
  optimizeType: optimizeTypeSchema,
});
export type BaseBenchResult = v.InferOutput<typeof baseBenchResultSchema>;

export const initializationResultSchema = v.object({
  ...baseBenchResultSchema.entries,
  type: v.literal("initialization"),
});
export type InitializationResult = v.InferOutput<typeof initializationResultSchema>;

export const validationResultSchema = v.object({
  ...baseBenchResultSchema.entries,
  type: v.literal("validation"),
});
export type ValidationResult = v.InferOutput<typeof validationResultSchema>;

export const parsingResultSchema = v.object({
  ...baseBenchResultSchema.entries,
  type: v.literal("parsing"),
  errorType: errorTypeSchema,
});
export type ParsingResult = v.InferOutput<typeof parsingResultSchema>;

const standardResultSchema = v.object({
  ...baseBenchResultSchema.entries,
  errorType: errorTypeSchema,
  type: v.literal("standard"),
});
export type StandardResult = v.InferOutput<typeof standardResultSchema>;

const jsonSchemaResultSchema = v.object({
  // no optimizeType - JIT/precompiled describes how a library validates, not how it converts
  ...v.omit(baseBenchResultSchema, ["optimizeType"]).entries,
  type: v.literal("jsonSchema"),
  target: jsonSchemaTargetSchema,
  direction: jsonSchemaDirectionSchema,
  source: jsonSchemaSourceSchema,
  standardJsonSchema: standardJsonSchemaSupportSchema,
  /** The JSON schema the library generated, so it can be compared with the others. */
  jsonSchema: v.string(),
});
export type JsonSchemaResult = v.InferOutput<typeof jsonSchemaResultSchema>;

export const jsonSchemaCombinationSchema = v.object({
  target: jsonSchemaTargetSchema,
  direction: jsonSchemaDirectionSchema,
});
export type JsonSchemaCombination = v.InferOutput<typeof jsonSchemaCombinationSchema>;

/** Which targets and directions a library can convert, and why it refuses the rest. */
export const jsonSchemaSupportResultSchema = v.object({
  id: v.string(),
  libraryName: v.string(),
  version: v.string(),
  note: v.optional(v.string()),
  source: jsonSchemaSourceSchema,
  standardJsonSchema: standardJsonSchemaSupportSchema,
  unsupported: v.array(
    v.object({
      ...jsonSchemaCombinationSchema.entries,
      reason: v.string(),
    }),
  ),
});
export type JsonSchemaSupportResult = v.InferOutput<typeof jsonSchemaSupportResultSchema>;

const stringResultSchema = v.object({
  ...baseBenchResultSchema.entries,
  type: v.literal("string"),
});
export type StringResult = v.InferOutput<typeof stringResultSchema>;

export const codecResultSchema = v.object({
  ...v.omit(baseBenchResultSchema, ["snippet", "mean"]).entries,
  encode: v.object({
    snippet: v.string(),
    mean: v.number(),
  }),
  decode: v.object({
    snippet: v.string(),
    mean: v.number(),
  }),
  type: v.literal("codec"),
  acceptsUnknown: v.optional(v.boolean()),
});

export type CodecResult = v.InferOutput<typeof codecResultSchema>;

export type BenchResult = OneOf<
  | InitializationResult
  | ValidationResult
  | ParsingResult
  | StandardResult
  | JsonSchemaResult
  | StringResult
>;

export const benchResultsSchema = v.object({
  initialization: v.array(initializationResultSchema),
  parsing: v.object(v.entriesFromList(dataTypeSchema.options, v.array(parsingResultSchema))),
  validation: v.object(v.entriesFromList(dataTypeSchema.options, v.array(validationResultSchema))),
  standard: v.object(v.entriesFromList(dataTypeSchema.options, v.array(standardResultSchema))),
  jsonSchema: v.array(jsonSchemaResultSchema),
  jsonSchemaSupport: v.array(jsonSchemaSupportResultSchema),
  string: v.object(
    v.entriesFromList(
      stringFormatSchema.options,
      v.object(v.entriesFromList(dataTypeSchema.options, v.array(stringResultSchema))),
    ),
  ),
  codec: v.array(codecResultSchema),
});
export type BenchResults = v.InferOutput<typeof benchResultsSchema>;
export const getEmptyResults = (): BenchResults => ({
  initialization: [],
  parsing: { valid: [], invalid: [] },
  validation: { valid: [], invalid: [] },
  standard: { valid: [], invalid: [] },
  jsonSchema: [],
  jsonSchemaSupport: [],
  string: unsafeFromEntries(
    stringFormatSchema.options.map((format) => [format, { valid: [], invalid: [] }]),
  ),
  codec: [],
});

export const minifyTypeSchema = v.picklist(["minified", "unminified"]);
export type MinifyType = v.InferOutput<typeof minifyTypeSchema>;

export const downloadResultSchema = v.object({
  fileName: v.string(),
  libraryName: v.string(),
  version: v.string(),
  note: v.optional(v.string()),
  bytes: v.number(),
  gzipBytes: v.number(),
});
export type DownloadResult = v.InferOutput<typeof downloadResultSchema>;

export const downloadResultsSchema = v.object({
  minified: v.array(downloadResultSchema),
  unminified: v.array(downloadResultSchema),
});
export type DownloadResults = v.InferOutput<typeof downloadResultsSchema>;

const resultTypeSchema = v.picklist(["success", "not an error", "no stack", "not found"]);

export const stackResultSchema = v.object({
  type: resultTypeSchema,
  libraryName: v.string(),
  version: v.string(),
  snippet: v.string(),
  frame: v.optional(v.number()),
  output: v.string(),
  lineCount: v.number(),
});
export type StackResult = v.InferOutput<typeof stackResultSchema>;
